import { useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import DashboardLayout from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  ArrowLeft,
  CheckCircle2,
  Copy,
  Download,
  Loader2,
  Sparkles,
} from "lucide-react";
import MembershipCardPreview from "@/components/MembershipCardPreview";
import { fmtDate } from "@/lib/api";
import { downloadCardImage, cardFileName } from "@/lib/downloadCard";

export default function AgentCardSuccess() {
  const { state } = useLocation();
  const navigate = useNavigate();
  const membership = state?.membership;
  const cardRef = useRef(null);
  const [downloading, setDownloading] = useState(false);

  if (!membership) {
    return (
      <DashboardLayout>
        <div className="py-20 text-center">
          <p className="text-muted-foreground">No membership data found.</p>
          <Link to="/agent/new">
            <Button className="mt-4 bg-gradient-primary text-primary-foreground rounded-xl">
              Sell a new membership
            </Button>
          </Link>
        </div>
      </DashboardLayout>
    );
  }

  const downloadCard = async () => {
    if (!cardRef.current) return;
    setDownloading(true);
    try {
      await downloadCardImage(
        cardRef.current,
        cardFileName(membership.membership_number, membership.patient_name)
      );
      toast.success("Card downloaded");
    } catch {
      toast.error("Could not download card");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="flex items-center gap-3 mb-6">
        <Link
          to="/agent"
          className="inline-flex items-center gap-2 text-xs text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> Back to dashboard
        </Link>
      </div>

      <div className="max-w-2xl mx-auto">
        <Card className="border-success/30 shadow-soft animate-scale-in">
          <CardContent className="p-6 sm:p-8">
            <div className="flex items-start gap-4">
              <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-success text-success-foreground shadow-elegant">
                <CheckCircle2 className="h-6 w-6" />
              </span>
              <div className="flex-1">
                <Badge className="bg-success/15 text-success border border-success/30 hover:bg-success/15 mb-2">
                  Payment successful
                </Badge>
                <h1 className="font-display text-xl sm:text-2xl font-bold text-foreground tracking-tight">
                  You just earned +₹{Number(membership.commission_amount || 0).toFixed(2)}!
                </h1>
                <p className="text-sm text-muted-foreground mt-1">
                  Card issued for{" "}
                  <span className="font-medium text-foreground">{membership.patient_name}</span>{" "}
                  and {membership.whatsapp_sent ? "sent on WhatsApp" : "is ready to share"}.
                </p>
              </div>
            </div>

            <div className="mt-6 flex justify-center">
              <MembershipCardPreview
                innerRef={cardRef}
                variant="flat"
                name={membership.patient_name}
                mobile={membership.patient_mobile}
                membershipNumber={membership.membership_number}
                issued={fmtDate(membership.issued_at)}
                expires={fmtDate(membership.expires_at)}
              />
            </div>

            <div className="mt-5">
              <Button
                onClick={downloadCard}
                disabled={downloading}
                className="w-full h-11 bg-gradient-primary text-primary-foreground rounded-xl shadow-elegant"
              >
                {downloading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Download className="h-4 w-4" />
                )}
                {downloading ? "Preparing image…" : "Download card as image"}
              </Button>
            </div>

            <div className="mt-5 rounded-xl border border-border bg-muted/40 p-4 flex items-center justify-between gap-3">
              <div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
                  Membership number
                </div>
                <div className="font-mono-card text-lg font-semibold tracking-wider text-foreground">
                  {membership.membership_number}
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  navigator.clipboard.writeText(membership.membership_number);
                  toast.success("Copied");
                }}
              >
                <Copy className="h-4 w-4" /> Copy
              </Button>
            </div>

            <div className="mt-5 grid sm:grid-cols-2 gap-3">
              <Button
                onClick={() => navigate("/agent/new")}
                className="h-11 bg-gradient-primary text-primary-foreground rounded-xl shadow-elegant"
              >
                <Sparkles className="h-4 w-4" /> Sell another card
              </Button>
              <Button
                variant="outline"
                className="h-11 rounded-xl"
                onClick={() => navigate("/agent")}
              >
                Back to dashboard
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
